<script>
import Common from '../../../common/Common.vue'
import Services from '../../../common/Services.vue'
import { applySessionScope } from '../../../utils/scope'
import { clampTimelineRange, cleanTimelineRow } from '../../../utils/timeline'
import moment from 'moment-timezone'

export default {
    init: async payload => {
        await payload.initRangeDate()
        payload.userLocale = payload.$i18n.locale === 'pt' ? 'pt-br' : 'en'

        const role = await Common.getSubtype(payload)
        payload.role = role

        payload.isSuperAdminMaster = await Common.isSuperAdminMaster(payload)

        await payload.initTable()

        if (role === 'SUPER_ADMIN_MASTER') {
            payload.accounts = await Services.filterAccounts(payload)
        } else if (role === 'ADMIN' || role === 'MANAGER') {
            payload.clients = await Services.filterClients(payload)
        } else if (role === 'MANAGER' || role === 'OPERATOR' || role === 'AUDITOR') {
            const client = await Common.getClientId(payload)
            payload.filters.client = client
            payload.sites = await Services.filterSites(payload)
        }
    },
    methods: {
        async filter() {
            if (this.isLoading) return

            if (this.role === 'SUPER_ADMIN_MASTER' && !this.filters.account) {
                Common.show(this, 'top-right', 'warn', this.$t('str.charts.select.account.required'))
                return
            }

            if (!applySessionScope(this, this.filters)) return

            this.isLoading = true

            if (this.filters.status === 'EXPIRED') {
                this.filters.isDescSortByStartDate = true
                this.filters.isSortByStartDate = false
            } else {
                this.filters.isSortByStartDate = true
                this.filters.isDescSortByStartDate = false
            }

            if (this.filters.status === 'COMPLETE' || this.filters.status === 'INCOMPLETE') {
                this.filters.substatus = this.filters.status
            } else {
                this.filters.substatus = ''
            }

            try {
                const page = await Services.getAlertOccurrencesPage(this, this.filters)
                this.items = page.results
                this.totalItems = page.totalCount
            } catch (_error) {
                this.items = []
                this.totalItems = 0
                Common.show(this, 'top-right', 'danger', this.$t('str.error_loading_data'))
            } finally {
                this.isLoading = false
            }
        },

        filterFromFirstPage() {
            this.currentPage = 1
            this.filters.skip = 1
            this.filter()
        },

        onPageChange(newPage) {
            this.currentPage = newPage.currentPage
            this.filters.skip = this.currentPage
            this.filter()
        },
        onPerPageChange(newPerPage) {
            this.paginationOptions.perPage = newPerPage.currentPerPage
            this.filters.limit = newPerPage.currentPerPage
            this.filters.skip = 1
            this.currentPage = 1
            this.filter()
        },
        async initTable() {
            this.columns = [
                {
                    label: this.$t('str.timeline.alertOccurrences.column.account'),
                    field: 'account',
                    width: '15%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.timeline.alertOccurrences.column.client'),
                    field: 'client',
                    width: '15%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.timeline.alertOccurrences.column.site'),
                    field: 'site',
                    width: '15%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.timeline.alertOccurrences.column.equipment'),
                    field: 'equipment',
                    width: '15%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.timeline.alertOccurrences.column.scheduledAt'),
                    field: 'scheduledAt',
                    width: '15%',
                    sortable: true,
                    tdClass: 'text-nowrap',
                    thClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.timeline.alertOccurrences.column.status'),
                    field: 'status',
                    width: '10%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
                {
                    label: this.$t('str.timeline.alertOccurrences.column.respondedAt'),
                    field: 'respondedAt',
                    width: '15%',
                    sortable: true,
                    thClass: 'text-nowrap',
                    tdClass: 'text-nowrap',
                },
            ]

            this.paginationOptions = {
                enabled: true,
                mode: 'pages',
                perPage: 10,
                position: 'bottom',
                perPageDropdown: [10, 20, 50, 100],
                dropdownAllowAll: false,
                setCurrentPage: 1,
                jumpFirstOrLast: true,
                firstLabel: this.$t('str.table.pagination.first.page'),
                lastLabel: this.$t('str.table.pagination.last.page'),
                nextLabel: this.$t('str.table.pagination.next.page'),
                prevLabel: this.$t('str.table.pagination.prev.page'),
                rowsPerPageLabel: this.$t('str.table.pagination.rows.per.page.lavel'),
                ofLabel: this.$t('str.table.pagination.of.label.page'),
                pageLabel: this.$t('str.table.pagination.page'),
                allLabel: this.$t('str.table.pagination.all.label'),
            }
        },
        initRangeDate: async function () {
            const startDate = moment().tz(Common.getAccountTimezone()).subtract(0, 'days')
            const endDate = moment().tz(Common.getAccountTimezone())
            const today = moment().tz(Common.getAccountTimezone())
            const yesterday = moment().tz(Common.getAccountTimezone()).subtract(1, 'days')
            const thisMonthStart = moment().tz(Common.getAccountTimezone()).startOf('month')

            this.dateRange = {
                opens: 'right',
                singleDatePicker: false,
                timePicker: false,
                timePicker24Hour: false,
                showWeekNumbers: false,
                showDropdowns: true,
                autoApply: false,
                linkedCalendars: true,
                closeOnEsc: true,
                range: {
                    startDate: startDate,
                    endDate: endDate,
                },
                maxDate: moment().tz(Common.getAccountTimezone()).endOf('day').toDate(),
                sampleLocaleData: {
                    direction: 'ltr',
                    format: this?.userLocale === 'pt-br' ? 'dd/mm/yyyy' : 'mm/dd/yyyy',
                    separator: ' - ',
                    applyLabel: this.$t('str.apply'),
                    cancelLabel: this.$t('str.cancel'),
                    weekLabel: 'W',
                        ranges: {
                        [this.$t('str.date.today')]: [today, today],
                        [this.$t('str.date.yesterday')]: [yesterday, yesterday],
                        [this.$t('str.date.this.month')]: [thisMonthStart, today],
                        [this.$t('str.date.last.month')]: [moment().tz(Common.getAccountTimezone()).subtract(1, 'month').startOf('month'), moment().tz(Common.getAccountTimezone()).subtract(1, 'month').endOf('month')],
                    },
                    daysOfWeek: [
                        this.$t('str.abbreviation.sunday'),
                        this.$t('str.abbreviation.monday'),
                        this.$t('str.abbreviation.tuesday'),
                        this.$t('str.abbreviation.wednesday'),
                        this.$t('str.abbreviation.thursday'),
                        this.$t('str.abbreviation.friday'),
                        this.$t('str.abbreviation.saturday'),
                    ],
                    monthNames: [
                        this.$t('str.abbreviation.january'),
                        this.$t('str.abbreviation.february'),
                        this.$t('str.abbreviation.march'),
                        this.$t('str.abbreviation.april'),
                        this.$t('str.abbreviation.may'),
                        this.$t('str.abbreviation.june'),
                        this.$t('str.abbreviation.july'),
                        this.$t('str.abbreviation.august'),
                        this.$t('str.abbreviation.september'),
                        this.$t('str.abbreviation.october'),
                        this.$t('str.abbreviation.november'),
                        this.$t('str.abbreviation.december'),
                    ],
                    firstDay: 0,
                },
            }
        },
        updateRangeDate: function (start, end) {
            const startDate = moment(start).tz(Common.getAccountTimezone()).subtract(0, 'days')
            const endDate = moment(end).tz(Common.getAccountTimezone())

            this.dateRange.range = {
                startDate: startDate,
                endDate: endDate,
            }
        },
        selectItem(params) {
            this.selectedItem = cleanTimelineRow(params.row)
            this.$bvModal.show('alertOccurrenceDetailModal')
        },
        changeAccount: async function () {
            const account = this.filters.account

            if (account === '') {
                this.filters.client = ''
                this.filters.site = ''
                this.filters.equipment = ''
                this.equipments = []
            }
            this.clients = await Services.getClientsByAccount(this, account)
        },
        changeClient: async function () {
            const client = this.filters.client

            if (client === '') {
                this.filters.site = ''
                this.filters.equipment = ''
                this.equipments = []
            }

            this.sites = await Services.getSitesByClient(this, client)
        },
        changeSite: async function () {
            const site = this.filters.site

            if (!site) {
                this.filters.equipment = ''
                this.equipments = []
                return
            }

            const body = {
                account: this.filters.account || null,
                client: this.filters.client || null,
                site: this.filters.site || null,
            }

            try {
                this.equipments = await Services.getEquipmentsBySite(this, body)
            } catch (err) {
                this.equipments = []
            }
        },
        async updateValues(d) {
            const accountTimezone = Common.getAccountTimezone()
            const { startDate, endDate, wasClamped } = clampTimelineRange(d.startDate, d.endDate, accountTimezone)

            if (wasClamped) {
                Common.show(this, 'top-right', 'warn', this.$t('str.charts.date.range.max'))
            }

            this.dateRange.range.startDate = startDate
            this.dateRange.range.endDate = endDate
            this.filters.startDate = startDate
            this.filters.endDate = endDate
        },
        getStatusName: Common.getEventStatusName,
        formatDate: Common.formatDateAndTime,
    },
}
</script>
